import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";
import axios from "axios";
import { API, useRequestModal, resolveImageUrl } from "../App";

// Hero image from user's uploaded asset
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_dce94b65-0fb3-4a6b-88b3-40e5eece8347/artifacts/2jvpbmio_5906CE43-1D06-4BB5-A679-8B950EF87230.png";

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingCatalog, setDownloadingCatalog] = useState(false);
  const { openModal } = useRequestModal();

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?featured_only=true`);
        setFeaturedProducts(response.data);
      } catch (error) {
        console.error("Error fetching featured products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  const handleDownloadCatalog = async () => {
    setDownloadingCatalog(true);
    try {
      const response = await axios.get(`${API}/catalog/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'INTROZZI_Catalog.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading catalog:", error);
    } finally {
      setDownloadingCatalog(false);
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Hero Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Tailored stone basin" 
            className="w-full h-full object-cover"
            data-testid="hero-image"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
        </div>

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 text-center px-6 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <h1 
            className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-6"
            data-testid="hero-headline"
          >
            Quiet refinement for the bathroom.
          </h1>
          <p className="text-gray-400 text-base sm:text-lg font-light max-w-xl mx-auto mb-10">
            Crafted with precision. Designed for silence.
          </p>
          <Link 
            to="/collections"
            className="inline-flex items-center gap-3 border border-white rounded-full text-white px-8 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300"
            data-testid="explore-products-btn"
          >
            Explore Collection
            <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <div className="w-px h-16 bg-gradient-to-b from-white/0 via-white/30 to-white/0" />
        </motion.div>
      </section>

      {/* Brand Statement */}
      <section className="py-24 sm:py-32 lg:py-48 px-6 sm:px-12 lg:px-24 bg-black">
        <motion.div 
          className="max-w-4xl"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-8">
            The Essence
          </p>
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-relaxed mb-8">
            INTROZZI represents the pinnacle of bathroom design, where every surface tells a story of material excellence and timeless sophistication.
          </h2>
          <p className="text-gray-400 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            Our collections are born from a deep reverence for natural materials and an unwavering commitment to artisanal craftsmanship. Each piece is designed to transform everyday rituals into moments of quiet refinement.
          </p>
        </motion.div>
      </section>

      {/* Featured Products */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A]" data-testid="featured-products-section">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">
                Featured
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl font-light text-white">
                Selected Works
              </h2>
            </div>
            <Link 
              to="/collections"
              className="hidden sm:flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm tracking-wider uppercase"
            >
              View All
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-[#121212]" />
                  <div className="mt-6 h-4 bg-[#121212] w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {featuredProducts.map((product, index) => (
                <motion.article
                  key={product.id}
                  className="group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`featured-product-${product.slug}`}
                >
                  <Link to={`/products/${product.slug}`}>
                    <div className="relative aspect-[4/5] overflow-hidden bg-[#121212]">
                      <img 
                        src={resolveImageUrl(product.cover_image)} 
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                    </div>
                    <div className="mt-6">
                      <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                        {product.collection}
                      </p>
                      <h3 className="font-['Playfair_Display'] text-xl text-white group-hover:text-gray-300 transition-colors">
                        {product.name}
                      </h3>
                    </div>
                  </Link>
                  <button
                    onClick={() => openModal(product)}
                    className="mt-4 text-xs tracking-widest uppercase text-gray-400 hover:text-[#C5A059] transition-colors"
                    data-testid={`request-btn-${product.slug}`}
                  >
                    Submit a Request
                  </button>
                </motion.article>
              ))}
            </div>
          )}

          <Link 
            to="/collections"
            className="sm:hidden flex items-center justify-center gap-2 mt-12 text-gray-400 hover:text-white transition-colors text-sm tracking-wider uppercase"
          >
            View All Collections
            <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </motion.div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 sm:py-32 lg:py-48 px-6 sm:px-12 lg:px-24 bg-black" id="our-story">
        <motion.div
          className="max-w-4xl mb-16 lg:mb-24"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-6">
            Our Story
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-8">
            Crafted in Silence
          </h2>
          <p className="text-gray-400 text-lg sm:text-xl font-light leading-relaxed">
            INTROZZI was born from a profound respect for the traditions of craftsmanship 
            and a vision to redefine craftsmanship in the most intimate spaces of the home.
          </p>
        </motion.div>

        {/* Editorial Image */}
        <motion.div 
          className="relative h-[60vh] lg:h-[70vh] mb-24 overflow-hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_introzzi-craft/artifacts/tu8emqmw_060E52DD-4062-48BC-A49A-3449E5F35FFA.png" 
            alt="Lorenzo Introzzi, Founder"
            className="w-full h-full object-cover object-[center_60%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none" />
        </motion.div>

        {/* Philosophy Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-6">
                Philosophy
              </p>
              <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-light text-white mb-6">
                The Art of Restraint
              </h3>
              <p className="text-gray-400 font-light leading-relaxed">
                In a world of excess, we believe in the power of subtraction. Each INTROZZI piece 
                is distilled to its essential form—where every curve, every edge, every surface 
                serves a purpose. This is design that speaks through silence.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-6">
                Materials
              </p>
              <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-light text-white mb-6">
                Material Honesty
              </h3>
              <p className="text-gray-400 font-light leading-relaxed">
                We source the world's finest natural materials—ancient stones from Italian quarries, 
                precious metals shaped by master artisans, woods that carry centuries of history. 
                Each material is chosen for its inherent beauty and allowed to express its true nature.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16 lg:mb-24"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-6">
              Our Values
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl font-light text-white">
              What We Believe
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            {[
              {
                title: "Craftsmanship",
                description: "Every piece is handcrafted by skilled artisans who have dedicated their lives to mastering their craft. We honor tradition while embracing innovation."
              },
              {
                title: "Timelessness",
                description: "We design for permanence, not trends. Our pieces are meant to be passed down through generations, growing more beautiful with time and use."
              },
              {
                title: "Tactile Quality",
                description: "True quality is felt, not just seen. We obsess over textures, temperatures, and the sensory experience of interacting with our products."
              }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-500 font-light leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 sm:py-32 lg:py-48 px-6 sm:px-12 lg:px-24 bg-black">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-relaxed italic">
            "The bathroom is the most intimate space in any home. It deserves the same consideration 
            we give to any great work of architecture or art."
          </p>
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="w-8 h-px bg-[#C5A059]" />
            <span className="text-gray-500 text-sm tracking-wider uppercase">
              Lorenzo Introzzi, Founder
            </span>
            <span className="w-8 h-px bg-[#C5A059]" />
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 lg:py-48 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A] text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-8">
            Begin Your Journey
          </h2>
          <p className="text-gray-400 font-light max-w-xl mx-auto mb-12">
            Discover our complete collection of tailored bathroom fixtures and accessories, each piece crafted to elevate your daily rituals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/collections"
              className="inline-flex items-center gap-3 border border-white rounded-full text-white px-10 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300"
              data-testid="cta-explore-btn"
            >
              Explore Collections
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
            <button
              onClick={handleDownloadCatalog}
              disabled={downloadingCatalog}
              className="inline-flex items-center gap-3 border border-white/40 rounded-full text-white px-10 py-4 text-sm tracking-widest uppercase font-medium hover:border-white hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50"
              data-testid="download-catalog-btn"
            >
              <Download size={16} strokeWidth={1.5} />
              {downloadingCatalog ? "Generating..." : "Download Catalog"}
            </button>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 sm:px-12 lg:px-24 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <h3 className="font-['Playfair_Display'] text-2xl text-white mb-4">INTROZZI</h3>
              <p className="text-gray-500 text-sm font-light max-w-sm">
                Tailored bathroom fixtures and accessories. Crafted with precision, designed for silence.
              </p>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">Navigate</p>
              <ul className="space-y-3">
                <li><Link to="/collections" className="text-gray-400 hover:text-white transition-colors text-sm">Collections</Link></li>
                <li><a href="#our-story" className="text-gray-400 hover:text-white transition-colors text-sm">Our Story</a></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">Contact</p>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li>contact@introzzi.ch</li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-xs">&copy; 2022 INTROZZI. All rights reserved.</p>
            <p className="text-gray-700 text-[10px] mt-1">brand of idrotek &mdash; CHE-468.961.634</p>
            <div className="flex items-center gap-4">
              <span className="w-12 h-px bg-[#C5A059]" />
              {/* Hidden admin link */}
              <Link 
                to="/admin/login" 
                className="text-gray-700 hover:text-gray-500 transition-colors text-[10px]"
                data-testid="hidden-admin-link"
              >
                ●
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
